import type {
  AgentSessionCapabilities,
  AgentSessionEvent,
  AgentSessionViewModel,
} from '@inf-monkeys-tech/monkeys';

type SessionEventType = AgentSessionEvent['eventType'];
export type AgentWorkbenchEventOf<Type extends SessionEventType> = Extract<
  AgentSessionEvent,
  { eventType: Type }
>;

const EVENT_CAPABILITIES = {
  reasoning: 'reasoning',
  plan: 'plan',
  task: 'tasks',
  tool: 'tools',
  approval: 'approval',
  artifact: 'artifacts',
  usage: 'usage',
} as const satisfies Partial<
  Record<SessionEventType, keyof AgentSessionCapabilities>
>;

export interface AgentWorkbenchActivityModel {
  mode: AgentSessionViewModel['snapshot']['mode'];
  status: AgentSessionViewModel['status'];
  reasoning: AgentWorkbenchEventOf<'reasoning'>[];
  plans: AgentWorkbenchEventOf<'plan'>[];
  tasks: AgentWorkbenchEventOf<'task'>[];
  tools: AgentWorkbenchEventOf<'tool'>[];
  approvals: AgentWorkbenchEventOf<'approval'>[];
  artifacts: AgentWorkbenchEventOf<'artifact'>[];
  usage?: AgentWorkbenchEventOf<'usage'>;
  run?: AgentWorkbenchEventOf<'status'> & { resolvedDurationMs?: number };
}

function projectRun(events: AgentSessionEvent[]): AgentWorkbenchActivityModel['run'] {
  const statuses = events.filter(
    (event): event is AgentWorkbenchEventOf<'status'> => event.eventType === 'status',
  );
  const run = statuses.at(-1);
  if (!run) return undefined;
  if (run.payload.status !== 'stopped' || run.payload.durationMs !== undefined) return run;

  const runStatuses = run.runId
    ? statuses.filter((event) => event.runId === run.runId && event.sequence <= run.sequence)
    : statuses.filter((event) => event.sequence <= run.sequence);
  let previousStoppedIndex = -1;
  for (let index = runStatuses.length - 1; index >= 0; index -= 1) {
    const event = runStatuses[index];
    if (event.sequence < run.sequence && event.payload.status === 'stopped') {
      previousStoppedIndex = index;
      break;
    }
  }
  const started = runStatuses
    .slice(previousStoppedIndex + 1)
    .reverse()
    .find((event) => event.payload.status === 'running' || event.payload.status === 'queued');
  const startedAt = started?.payload.startedAt ?? started?.occurredAt;
  const stoppedAt = run.payload.stoppedAt ?? run.occurredAt;
  if (!startedAt || !stoppedAt) return run;
  const durationMs = Date.parse(stoppedAt) - Date.parse(startedAt);
  return Number.isFinite(durationMs) && durationMs >= 0
    ? { ...run, resolvedDurationMs: durationMs }
    : run;
}

function isVisible(
  event: AgentSessionEvent,
  capabilities: AgentSessionCapabilities,
): boolean {
  const capability = EVENT_CAPABILITIES[
    event.eventType as keyof typeof EVENT_CAPABILITIES
  ];
  return capability ? capabilities[capability] === true : true;
}

function latestBy<Type extends SessionEventType>(
  events: AgentSessionEvent[],
  eventType: Type,
  key: (event: AgentWorkbenchEventOf<Type>) => string,
): AgentWorkbenchEventOf<Type>[] {
  const latest = new Map<string, AgentWorkbenchEventOf<Type>>();
  for (const event of events) {
    if (event.eventType !== eventType) continue;
    const typedEvent = event as AgentWorkbenchEventOf<Type>;
    latest.set(key(typedEvent), typedEvent);
  }
  return [...latest.values()].sort(
    (left, right) => left.sequence - right.sequence,
  );
}

export function createAgentWorkbenchActivityModel(
  viewModel: AgentSessionViewModel,
): AgentWorkbenchActivityModel {
  const visibleEvents = viewModel.events.filter((event) =>
    isVisible(event, viewModel.snapshot.capabilities),
  );

  return {
    mode: viewModel.snapshot.mode,
    status: viewModel.status,
    reasoning: latestBy(
      visibleEvents,
      'reasoning',
      (event) => event.payload.reasoningId,
    ),
    plans: latestBy(
      visibleEvents,
      'plan',
      (event) => event.payload.planId,
    ),
    tasks: latestBy(
      visibleEvents,
      'task',
      (event) => event.payload.taskId,
    ),
    tools: latestBy(
      visibleEvents,
      'tool',
      (event) => event.payload.toolCallId,
    ),
    approvals: latestBy(
      visibleEvents,
      'approval',
      (event) => event.payload.approvalId,
    ),
    artifacts: latestBy(
      visibleEvents,
      'artifact',
      (event) => event.payload.artifactId,
    ),
    usage: [...visibleEvents]
      .reverse()
      .find(
        (event): event is AgentWorkbenchEventOf<'usage'> =>
          event.eventType === 'usage',
      ),
    run: projectRun(visibleEvents),
  };
}
